/**
 * Choosing the Deltek project for field work: type to filter, then pick.
 *
 * About a thousand projects is too many for a plain <select>, where typing a letter only jumps
 * to the first match. Here typing narrows the list: case-insensitive, anywhere in the name, and
 * the project's Ajera key when digits are typed (Deltek's job numbers are retired, so the key is
 * the only number a project has). Up/Down move through the list, Enter picks, Escape closes.
 *
 * Built on the ARIA combobox pattern rather than a library: it is one input and one list, and a
 * dependency for that would be the larger change. No rules here: the API decides which projects
 * exist and refuses a booking without one.
 */
import type { ProjectOption } from "@scheduler/shared";
import { useId, useMemo, useState, type KeyboardEvent } from "react";

/** Enough to scan; past this, typing a few more letters is quicker than scrolling. */
const SHOWN = 50;

export function ProjectPicker({ projects, failed, value, onChange }: {
  /** Null while loading. */
  projects: ProjectOption[] | null;
  /** The list could not be loaded. */
  failed: boolean;
  value: number | null;
  onChange: (ajeraKey: number | null) => void;
}) {
  const id = useId();
  const listId = `${id}-list`;
  const chosen = projects?.find((p) => p.ajeraKey === value) ?? null;
  const [text, setText] = useState(chosen?.name ?? "");
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(0);

  const matches = useMemo(() => {
    const q = text.trim().toLowerCase();
    const all = projects ?? [];
    if (!q || (chosen && text === chosen.name)) return all;
    return all.filter((p) => p.name.toLowerCase().includes(q) || String(p.ajeraKey).includes(q));
  }, [projects, text, chosen]);
  const shown = matches.slice(0, SHOWN);

  if (failed || (projects && projects.length === 0)) {
    return <p className="hint picker-empty" role="status">No projects available, contact HR.</p>;
  }

  function pick(p: ProjectOption) {
    onChange(p.ajeraKey);
    setText(p.name);
    setOpen(false);
  }

  function onKey(e: KeyboardEvent<HTMLInputElement>) {
    if (e.key === "ArrowDown" || e.key === "ArrowUp") {
      e.preventDefault();
      if (!open) { setOpen(true); setActive(0); return; }
      const step = e.key === "ArrowDown" ? 1 : -1;
      setActive((i) => Math.max(0, Math.min(shown.length - 1, i + step)));
    } else if (e.key === "Enter") {
      // Never submits the form from here: Enter in this box means "this one".
      e.preventDefault();
      const p = open ? shown[active] : undefined;
      if (p) pick(p);
    } else if (e.key === "Escape") {
      setOpen(false);
    }
  }

  return (
    <div className="picker">
      <input
        type="text"
        role="combobox"
        aria-expanded={open && shown.length > 0}
        aria-controls={listId}
        aria-autocomplete="list"
        aria-activedescendant={open && shown[active] ? `${id}-opt-${shown[active].ajeraKey}` : undefined}
        aria-label="Project"
        placeholder={projects === null ? "Loading projects…" : "Type part of the project name…"}
        disabled={projects === null}
        value={text}
        onChange={(e) => {
          setText(e.target.value);
          setOpen(true);
          setActive(0);
          // Typing again un-chooses: what is in the box must be what is sent.
          if (value !== null) onChange(null);
        }}
        onFocus={() => setOpen(true)}
        onBlur={() => setOpen(false)}
        onKeyDown={onKey}
      />
      {open && projects ? (
        <ul id={listId} role="listbox" className="picker-list">
          {shown.length === 0 ? (
            <li className="picker-none" aria-disabled="true">No project matches “{text.trim()}”.</li>
          ) : shown.map((p, i) => (
            <li
              key={p.ajeraKey}
              id={`${id}-opt-${p.ajeraKey}`}
              role="option"
              aria-selected={i === active}
              // mousedown, not click: it lands before the input's blur closes the list.
              onMouseDown={(e) => { e.preventDefault(); pick(p); }}
              onMouseEnter={() => setActive(i)}
            >
              {p.name}
              <span className="sub">{p.projectManager ? `PM ${p.projectManager.name}` : "No PM on record"}</span>
            </li>
          ))}
          {matches.length > SHOWN ? (
            <li className="picker-none" aria-disabled="true">Showing {SHOWN} of {matches.length}. Keep typing to narrow it.</li>
          ) : null}
        </ul>
      ) : null}
    </div>
  );
}
